import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { animateSvg, cleanupSvgAnimations } from '../../utils/svgAnimator';
import { useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { useAiMentorChat } from '../../hooks/useAiMentorChat';
import { useLipSync } from '../../hooks/useLipSync';
import { Course, Lesson, LessonTopic } from '../../types/courses.types';
import { SpeechQueueManager, stopGlobalTTS } from '../../utils/speechQueueManager';
import { TextNormalizer } from '../../utils/textNormalizer';
import { useFeatures } from '../../hooks/useFeatures';
import { useAuthStore } from '../../store/authStore';
import { Play, Pause, Minimize, Maximize } from 'lucide-react';
import { coursesService } from '../../services/courses.service';
import { authService } from '../../services/auth.service';

const API_BASE = (import.meta.env.PROD && (import.meta.env.VITE_API_BASE_URL === 'http://localhost:8080' || !import.meta.env.VITE_API_BASE_URL)) ? '' : (import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080');

interface CinAliPlayerProps {
  lesson: Lesson & { topics?: LessonTopic[] };
  course: Course;
  onCompleteLesson?: () => void;
  isLessonCompleted?: boolean;
  courseProgressPercent?: number;
  nextLesson?: Lesson | null;
  notes?: string;
  setNotes?: (val: string) => void;
  onSaveNote?: () => Promise<void>;
  isSavingNote?: boolean;
  noteSaveStatus?: string | null;
  completionResult?: any;
  topics?: LessonTopic[];
  activeTopicVideoId?: string | null;
  setActiveTopicVideoId?: (id: string | null) => void;
}

// ── Tipler ────────────────────────────────────────────────────────────────
interface Slide {
  markdown: string;
  svgVisual?: string;
  visualDescription?: string;
  speechText: string;
  sentences?: { text: string; audioBase64: string }[];
  svgLoading?: boolean;
}

interface QAMessage {
  id: string;
  role: 'student' | 'teacher';
  text: string;
}

// ── FIX 1: Görsel Tanımı ve SVG ayıkla ─────────────────────
function extractVisualAndSvg(raw: string, lessonTitle?: string): { svg: string | undefined; visualDescription: string | undefined; cleanMarkdown: string } {
  if (!raw) return { svg: undefined, visualDescription: undefined, cleanMarkdown: '' };

  let cleanMarkdown = raw;
  let svg: string | undefined = undefined;
  let visualDescription: string | undefined = undefined;

  // 1. [GÖRSEL_TANIMI: ...], [GÖRSEL: ...], **Görsel Tanımı:** gibi tüm varyantları yakala
  const visualMatch = cleanMarkdown.match(/(?:\[(?:GÖRSEL|GORSEL|ŞEKİL|SEKIL|ÇİZİM|CIZIM|DİYAGRAM|DIAGRAM|TABLO|PANEL|SAHNE)[-_ ]?(?:TANIMI|tanimi|AÇIKLAMASI|aciklamasi|BETİMLEMESİ|betimlemesi)?:\s*([\s\S]*?)\]|\*\*(?:Görsel|Görsel Tanımı|Şekil Tanımı)\*\*:\s*([^\n]+))/i);
  if (visualMatch) {
    visualDescription = (visualMatch[1] || visualMatch[2] || '').trim();
  }

  // Tüm görsel, şekil, sahne ve mizanpaj tanımlarını temizle
  cleanMarkdown = cleanMarkdown
    .replace(/\[(?:GÖRSEL|GORSEL|ŞEKİL|SEKIL|ÇİZİM|CIZIM|DİYAGRAM|DIAGRAM|TABLO|PANEL|SAHNE|ANİMASYON|ANIMASYON|GRAFİK|GRAFIK|SÜTUN|SUTUN)[-_ ]?(?:TANIMI|tanimi|AÇIKLAMASI|aciklamasi|BETİMLEMESİ|betimlemesi)?:\s*[\s\S]*?\]/gi, '')
    .replace(/\[(?:GÖRSEL|GORSEL|ŞEKİL|SEKIL|ÇİZİM|CIZIM|DİYAGRAM|DIAGRAM|TABLO|PANEL|SAHNE)[-_ ]?[^\]]*:[\s\S]*/gi, '')
    .replace(/\*\*(?:Görsel|Görsel Tanımı|Şekil Tanımı)\*\*:[^\n]*/gi, '')
    // Sütun, çerçeve, renk veya mizanpaj içeren her türlü köşeli parantez bloğunu sil
    .replace(/\[[^\]]*#[0-9a-fA-F]{3,8}[\s\S]*?\]/gi, '')
    .replace(/\[[^\]]*(?:çerçeveli|kutucuğu|yer alır|sütun|diyagram|mizanpaj|şablon)[\s\S]*?\]/gi, '')
    // Tamamlanmamış / yarım kalmış görsel bloklarını sil
    .replace(/\[[^\]]*(?:görsel|şekil|çizim|diyagram|sütun|çerçeve|kutucuğu|yer alır)[\s\S]*$/gi, '')
    // Metin içine sızmış HEX renk kodlarını ve "mor çerçeveli", "yeşil renkli" gibi yapay etiketleri sil
    .replace(/#[0-9a-fA-F]{6}\b\s*(?:mor|yeşil|mavi|kırmızı|sarı|turuncu|pembe|gri|siyah|beyaz)?\s*(?:çerçeveli|renkli|çizgili)?/gi, '')
    .replace(/#[0-9a-fA-F]{3,8}\b/gi, '');

  // 2. Tam veya yarım <svg>...</svg> bloklarını ayıkla ve metinden tamamen sil
  const completeSvgMatch = cleanMarkdown.match(/<svg[\s\S]*?<\/svg>/i);
  if (completeSvgMatch) {
    svg = completeSvgMatch[0].trim();
  }
  cleanMarkdown = cleanMarkdown
    .replace(/<svg[\s\S]*?<\/svg>/gi, '')
    .replace(/<svg[\s\S]*/gi, '');

  // 3. Kaçak SVG / XML etiketlerini ve bozuk parametre parçalarını metinden temizle
  cleanMarkdown = cleanMarkdown
    .replace(/```(?:svg|xml|html|svg-xml|xml-svg)?[\s\S]*?```/gi, '')
    .replace(/<(?:rect|circle|path|text|g|defs|marker|polygon|line|svg)[^>]*>[\s\S]*?<\/(?:rect|circle|path|text|g|defs|marker|polygon|line|svg)>/gi, '')
    .replace(/<(?:rect|circle|path|text|g|defs|marker|polygon|line)[^>]*\/?>/gi, '')
    .replace(/font-family="[^"]*"[^>]*>/gi, '')
    .replace(/fill="[^"]*"[^>]*>/gi, '')
    .replace(/```[^`]*/g, '')
    .replace(/```/g, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();

  // 4. Eğer yapay zeka görsel tanımı koymayı unuttuysa, slayt başlığı ve metninden otomatik görsel tanımı üret
  if (!visualDescription && cleanMarkdown.trim()) {
    const titleMatch = cleanMarkdown.match(/##\s*([^\n]+)/);
    const slideHeading = titleMatch ? titleMatch[1].trim() : (lessonTitle || 'Ders Konusu');
    const cleanBody = cleanMarkdown.replace(/##[^\n]+/g, '').replace(/[*_#`]/g, '').trim();
    const firstSentence = cleanBody.split(/[.!?]/)[0]?.trim() || '';
    visualDescription = `${slideHeading}: ${firstSentence}. Bu kavramı anlatan sade ve eğitici diyagram.`;
  }

  // 5. SVG varsa geçerli bir <svg>...</svg> olduğunu doğrula
  if (svg && (!svg.startsWith('<svg') || !svg.endsWith('</svg>'))) {
    const match = svg.match(/<svg[\s\S]*?<\/svg>/i);
    svg = match ? match[0].trim() : undefined;
  }

  return { svg, visualDescription, cleanMarkdown };
}

// ── SVG Tema & Keskin Hat Uyarlayıcı ───────────────────────
export function adaptSvgToLightSharpTheme(rawSvg: string): string {
  if (!rawSvg) return rawSvg;
  let svg = rawSvg
    .replace(/^```(?:svg|xml|html)?\s*/i, '')
    .replace(/\s*```$/, '')
    .trim();

  // 1. Keskin köşeler (Yumuşatılmış rx/ry değerlerini 0 yap)
  svg = svg.replace(/\brx="[^"]*"/gi, 'rx="0"').replace(/\bry="[^"]*"/gi, 'ry="0"');
  svg = svg.replace(/\brx='[^']*'/gi, "rx='0'").replace(/\bry='[^']*'/gi, "ry='0'");

  // 2. Koyu arka planları aydınlık/ferah tona çevir
  svg = svg.replace(/fill="(?:#090d16|#0b0f19|#070a13|#0d1117|#020617|#000000|#18181b|#181d2c|#111827|#0f172a)"/gi, 'fill="#FFFFFF"');
  svg = svg.replace(/fill='(?:#090d16|#0b0f19|#070a13|#0d1117|#020617|#000000|#18181b|#181d2c|#111827|#0f172a)'/gi, "fill='#FFFFFF'");

  // 3. Koyu kart içlerini ferah açık gri/pastel tonlara çevir
  svg = svg.replace(/fill="(?:#131d2e|#1e293b|#1f2937|#334155)"/gi, 'fill="#F8FAFC"');
  svg = svg.replace(/fill='(?:#131d2e|#1e293b|#1f2937|#334155)'/gi, "fill='#F8FAFC'");

  // 4. Koyu sınır çizgilerini açık griye çevir
  svg = svg.replace(/stroke="(?:#1e293b|#334155|#475569|#0f172a)"/gi, 'stroke="#CBD5E1"');
  svg = svg.replace(/stroke='(?:#1e293b|#334155|#475569|#0f172a)'/gi, "stroke='#CBD5E1'");

  // 5. Koyu arka plan için yazılmış beyaz/açık metinleri koyu laciverte çevir (yüksek kontrast)
  svg = svg.replace(/<text([^>]*?)fill="(?:#ffffff|#f8fafc|#f1f5f9|#e4e4e7|#e2e8f0|#cbd5e1|white)"/gi, '<text$1fill="#0F172A"');
  svg = svg.replace(/<text([^>]*?)fill='(?:#ffffff|#f8fafc|#f1f5f9|#e4e4e7|#e2e8f0|#cbd5e1|white)'/gi, "<text$1fill='#0F172A'");

  // 6. Cyan başlıkları açık zemin için daha okunaklı maviye uyarla
  svg = svg.replace(/fill="#38bdf8"/gi, 'fill="#0284C7"');
  svg = svg.replace(/fill='#38bdf8'/gi, "fill='#0284C7'");

  return svg;
}

function prepareSpeechText(markdown: string): string {
  if (!markdown) return '';
  return TextNormalizer.normalize(markdown);
}

// ── Slayt bölücü (offline mod için) ──────────────────────────────────────
function splitIntoSlides(displayText: string, audioText: string): Slide[] {
  if (!displayText.trim()) return [];
  const headerPattern = /(?=^##\s|^###\s)/m;
  const hasHeaders = /^##+\s/m.test(displayText);

  let displaySections = displayText.split(headerPattern).filter(s => s.trim());
  let audioSections   = audioText.split(headerPattern).filter(s => s.trim());

  if (!hasHeaders && displaySections.length <= 1) {
    const dp = displayText.split(/\n{2,}/).filter(s => s.trim());
    const ap = audioText.split(/\n{2,}/).filter(s => s.trim());
    displaySections = [];
    audioSections   = [];
    let cd = '', ca = '';
    for (let i = 0; i < dp.length; i++) {
      if (cd.length + dp[i].length > 400 && cd.length > 0) {
        displaySections.push(cd.trim());
        audioSections.push(ca.trim());
        cd = dp[i]; ca = ap[i] || dp[i];
      } else {
        cd += (cd ? '\n\n' : '') + dp[i];
        ca += (ca ? '\n\n' : '') + (ap[i] || dp[i]);
      }
    }
    if (cd.trim()) { displaySections.push(cd.trim()); audioSections.push(ca.trim()); }
  }

  return displaySections.map((md, idx) => {
    const { svg, visualDescription, cleanMarkdown } = extractVisualAndSvg(md.trim());
    return {
      markdown:   cleanMarkdown,
      svgVisual:  svg ? adaptSvgToLightSharpTheme(svg) : undefined,
      visualDescription: visualDescription,
      speechText: prepareSpeechText(audioSections[idx] || cleanMarkdown),
      svgLoading: visualDescription && !svg ? true : false,
    };
  });
}

function getMouthPath(openness: number): string {
  const cx = 100, y = 62, halfW = 14;
  return `M ${cx - halfW} ${y} Q ${cx} ${y + openness * 9} ${cx + halfW} ${y}`;
}

// ── Slayta Özel Dinamik Eğitici SVG Üretici (Fallback & Offline) ──────────
function createDynamicSlideSvg(
  slideIndex: number,
  slideHeading: string,
  markdown: string,
  lessonTitle: string
): string {
  const safeHeading = (slideHeading || lessonTitle || `Slayt ${slideIndex + 1}`).replace(/[<>&"]/g, '').substring(0, 48);

  // Slayt metninden konunun gerçek kavramlarını ve adımlarını çek
  const cleanBody = (markdown || '').replace(/##[^\n]+/g, '').replace(/[*_#`]/g, '').trim();
  const sentences = cleanBody.split(/[.!?\n]/).map(s => s.trim()).filter(s => s.length > 4);

  const leftTitle = sentences[0] ? sentences[0].substring(0, 26) : 'Temel Kavram & Model';
  const leftDetail1 = sentences[1] ? sentences[1].substring(0, 30) : 'Veri Yapısı ve Tanımlar';
  const leftDetail2 = sentences[2] ? sentences[2].substring(0, 30) : 'Özellikler & Mantıksal Kurallar';

  const rightTitle = sentences[3] ? sentences[3].substring(0, 26) : 'Uygulama & Akış';
  const rightDetail1 = sentences[4] ? sentences[4].substring(0, 30) : 'İşlem Adımları ve Metotlar';
  const rightDetail2 = sentences[5] ? sentences[5].substring(0, 30) : 'Sonuç Çıktısı & Başarı';

  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 560 320" width="100%" height="100%">
    <rect width="560" height="320" fill="#FFFFFF" rx="0" stroke="#CBD5E1" stroke-width="1.5"/>
    <rect x="50" y="14" width="460" height="34" fill="#F0F9FF" rx="0" stroke="#0284C7" stroke-width="1.2"/>
    <text x="280" y="36" fill="#0284C7" font-size="12" font-weight="bold" text-anchor="middle" font-family="Arial, sans-serif">${safeHeading}</text>

    <!-- Sol Kart: Konunun İlk Kavramı / Modeli -->
    <rect x="40" y="62" width="220" height="235" fill="#FFF7ED" rx="0" stroke="#EA580C" stroke-width="1.5"/>
    <rect x="55" y="75" width="190" height="26" fill="#EA580C" rx="0" opacity="0.15"/>
    <text x="150" y="93" fill="#C2410C" font-size="11" font-weight="bold" text-anchor="middle" font-family="Arial, sans-serif">${leftTitle}</text>
    <circle cx="150" cy="148" r="30" fill="rgba(234,88,12,0.12)" stroke="#EA580C" stroke-width="2"/>
    <text x="150" y="156" fill="#EA580C" font-size="22" text-anchor="middle">⚡</text>
    <rect x="55" y="195" width="190" height="32" fill="#FFFFFF" rx="0" stroke="#FDBA74" stroke-width="1"/>
    <text x="150" y="215" fill="#1E293B" font-size="10" font-weight="bold" text-anchor="middle" font-family="Arial, sans-serif">${leftDetail1}</text>
    <rect x="55" y="235" width="190" height="42" fill="#FFFFFF" rx="0" stroke="#FED7AA" stroke-width="1"/>
    <text x="65" y="253" fill="#64748B" font-size="9" font-family="Arial, sans-serif">• ${leftDetail2}</text>
    <text x="65" y="268" fill="#64748B" font-size="9" font-family="Arial, sans-serif">• Giriş Değerleri &amp; Kurallar</text>

    <!-- Bağlantı / Ok -->
    <circle cx="280" cy="175" r="16" fill="#F0F9FF" stroke="#0284C7" stroke-width="1.5"/>
    <text x="280" y="181" fill="#0284C7" font-size="16" text-anchor="middle" font-weight="bold">→</text>

    <!-- Sağ Kart: Konunun İkinci Kavramı / Uygulama & Çıktı -->
    <rect x="300" y="62" width="220" height="235" fill="#F0FDF4" rx="0" stroke="#16A34A" stroke-width="1.5"/>
    <rect x="315" y="75" width="190" height="26" fill="#16A34A" rx="0" opacity="0.15"/>
    <text x="410" y="93" fill="#15803D" font-size="11" font-weight="bold" text-anchor="middle" font-family="Arial, sans-serif">${rightTitle}</text>
    <circle cx="410" cy="148" r="30" fill="rgba(22,163,74,0.12)" stroke="#16A34A" stroke-width="2"/>
    <text x="410" y="156" fill="#16A34A" font-size="22" text-anchor="middle">🎯</text>
    <rect x="315" y="195" width="190" height="32" fill="#FFFFFF" rx="0" stroke="#86EFAC" stroke-width="1"/>
    <text x="410" y="215" fill="#1E293B" font-size="10" font-weight="bold" text-anchor="middle" font-family="Arial, sans-serif">${rightDetail1}</text>
    <rect x="315" y="235" width="190" height="42" fill="#FFFFFF" rx="0" stroke="#BBF7D0" stroke-width="1"/>
    <text x="325" y="253" fill="#64748B" font-size="9" font-family="Arial, sans-serif">• ${rightDetail2}</text>
    <text x="325" y="268" fill="#64748B" font-size="9" font-family="Arial, sans-serif">• Uygulama ve Doğrulama</text>
  </svg>`;
}

// ═══════════════════════════════════════════════════════════════════════════
export function CinAliPlayer({
  lesson, course, onCompleteLesson, isLessonCompleted,
  courseProgressPercent = 0, nextLesson = null,
  notes = '', setNotes, onSaveNote, isSavingNote = false,
  noteSaveStatus = null, completionResult,
  topics = [], activeTopicVideoId = null, setActiveTopicVideoId,
}: CinAliPlayerProps) {
  const navigate = useNavigate();
  const { hasFeature } = useFeatures();
  const { profile } = useAuthStore();
  const isAdmin = profile?.role === 'admin';
  const canAccessPremium = isAdmin || hasFeature('lesson.premium') || hasFeature('course.premium');

  const accessibleTopics = useMemo(() =>
    (lesson.topics || []).filter(t => !t.is_premium || canAccessPremium),
  [lesson.topics, canAccessPremium]);

  const studentContext = useMemo(() => ({
    currentCourse:  course.title,
    currentLesson:  lesson.title,
    goals: [
      'Sen bu dersin interaktif AI hocasısın.',
      'Kullanıcıya dersi sanki bir video sunumundaymış gibi anlat.',
      'Ders anlatımına başlarken KESİNLİKLE selamlama cümlesi KULLANMA. Doğrudan ilk konunun ## başlığıyla gir.',
      'Konunun arkasındaki temel MANTIĞI günlük hayattan benzetmeler ve pratik kod örnekleriyle öğret.',
      'İngilizce programlama terimlerinin yanına parantez içinde Türkçe okunuşlarını YAZMA.',
      ...(accessibleTopics.length > 0
        ? [`Bu derste KESİNLİKLE sadece şu alt konuları işle: ${accessibleTopics.map(t => `"${t.title}: ${t.description || ''}"`).join(', ')}.`]
        : []),
    ],
  }), [course.title, lesson.title, accessibleTopics]);

  const { messages, isLoading, isStreaming, sendMessage, isGlobalAudioPlaying, isQuotaExceeded, stopAudio } =
    useAiMentorChat(studentContext);
  const { mouthOpenness, isSpeaking, startSpeaking, stopSpeaking, onBoundary } = useLipSync();

  // ── UI State ───────────────────────────────────────────────────────────
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [chatInput,    setChatInput]    = useState('');
  const playerContainerRef  = useRef<HTMLDivElement>(null);
  const boardTextAreaRef    = useRef<HTMLDivElement>(null);
  const svgVisualRef        = useRef<HTMLDivElement>(null);
  const svgAnimCleanupRef   = useRef<(() => void) | undefined>(undefined);

  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  // ── Ders State ─────────────────────────────────────────────────────────
  const [slides,           setSlides]           = useState<Slide[]>([]);
  const [currentSlide,     setCurrentSlide]     = useState(0);
  const [displayedMarkdown,setDisplayedMarkdown] = useState('');
  const [mode,             setMode]             = useState<'idle'|'lesson'|'qa'>('idle');
  const [slideTransition,  setSlideTransition]  = useState<'in'|'out'|'none'>('none');
  const [isRequestingLesson,  setIsRequestingLesson]  = useState(false);
  const [isStreamingLoading,  setIsStreamingLoading]  = useState(false);
  const [qaOpen,       setQaOpen]       = useState(false);
  const [qaMessages,   setQaMessages]   = useState<QAMessage[]>([]);
  const [qaWaitingAI,  setQaWaitingAI]  = useState(false);
  const [qaAnswerDone, setQaAnswerDone] = useState(false);
  const [isPaused,     setIsPaused]     = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1.0);

  const [slideSvgs, setSlideSvgs] = useState<Record<number, string>>({});
  const slideSvgsRef = useRef<Record<number, string>>({});
  const processedMessageIdRef  = useRef<string|null>(null);
  const slidesRef              = useRef<Slide[]>([]);
  const currentSlideRef        = useRef(0);
  const speechQueueManagerRef  = useRef(new SpeechQueueManager());
  const isInterruptedRef       = useRef(false);
  const currentAudioRef        = useRef<HTMLAudioElement|null>(null);
  const resumeSlideIdxRef      = useRef(0);
  const resumeSentenceIdxRef   = useRef(0);
  const currentSentenceIdxRef  = useRef(0);
  const lessonAutoFinishedRef  = useRef(false);
  const audioQueueRef          = useRef<{text:string; audioBase64:string}[]>([]);
  const isPlayingQueueRef      = useRef(false);
  const streamActiveRef        = useRef(false);
  const svgRequestsRef         = useRef<Set<number>>(new Set());
  const waitingForSvgSlideIdxRef = useRef<number | null>(null);
  const pendingAudioSlideRef   = useRef<{ slideIndex: number; startSentenceIdx: number } | null>(null);
  const processAudioQueueRef   = useRef<() => void>(() => {});
  const playSlideAudioRef      = useRef<(slideIndex: number, startSentenceIdx?: number) => void>(() => {});

  const fetchSvgForSlide = useCallback(async (slideIndex: number, visualDescription: string) => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 45000); // 45 saniye zaman aşımı

    const targetSlide = slidesRef.current[slideIndex];
    const headingMatch = targetSlide?.markdown?.match(/##\s*([^\n]+)/);
    const slideHeading = headingMatch ? headingMatch[1].trim() : (slideIndex === 0 ? lesson.title : `${lesson.title} — Slayt ${slideIndex + 1}`);

    let enrichedDesc = visualDescription;
    if (!enrichedDesc || enrichedDesc.length < 15 || enrichedDesc.includes('Konu Görseli')) {
      const cleanBody = (targetSlide?.markdown || '').replace(/##[^\n]+/g, '').replace(/[*_#`]/g, '').trim();
      const firstSentence = cleanBody.split(/[.!?]/)[0]?.trim() || '';
      enrichedDesc = `${slideHeading}: ${firstSentence}. Bu konuyu anlatan detaylı eğitici infografik ve akış şeması.`;
    }

    try {
      const token = await authService.getAccessToken();
      if (!token) return;

      const res = await fetch(`${API_BASE}/api/ai/lesson/generate-svg`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          visualDescription: enrichedDesc,
          lessonTitle: slideHeading,
        }),
        signal: controller.signal,
      });

      if (!res.ok) throw new Error(`Görsel üretilemedi (HTTP ${res.status})`);
      const data = await res.json();

      let finalSvg = (data.svg || '').trim();
      if (finalSvg && !finalSvg.startsWith('<svg')) {
        const match = finalSvg.match(/<svg[\s\S]*?<\/svg>/i);
        if (match) finalSvg = match[0].trim();
      }

      if (finalSvg) {
        finalSvg = adaptSvgToLightSharpTheme(finalSvg);
        slideSvgsRef.current[slideIndex] = finalSvg;
        setSlideSvgs(prev => ({ ...prev, [slideIndex]: finalSvg }));

        setSlides(prev => {
          const updated = [...prev];
          if (updated[slideIndex]) {
            updated[slideIndex] = {
              ...updated[slideIndex],
              svgVisual: finalSvg,
              svgLoading: false,
            };
          }
          return updated;
        });

        // Bir sonraki slaytın görselini arka planda hemen önceden üretmeye başla (Sequential Prefetch)
        const nextSlideIdx = slideIndex + 1;
        setTimeout(() => {
          const nextSlide = slidesRef.current[nextSlideIdx];
          if (nextSlide && !nextSlide.svgVisual && !svgRequestsRef.current.has(nextSlideIdx)) {
            svgRequestsRef.current.add(nextSlideIdx);
            const nextHeadingMatch = nextSlide.markdown?.match(/##\s*([^\n]+)/);
            const nextHeading = nextHeadingMatch ? nextHeadingMatch[1].trim() : `${lesson.title} — Slayt ${nextSlideIdx + 1}`;
            const desc = nextSlide.visualDescription || `${nextHeading}. Bu konuyu anlatan eğitici infografik ve akış şeması.`;
            fetchSvgForSlide(nextSlideIdx, desc);
          }
        }, 150);

        if (pendingAudioSlideRef.current && pendingAudioSlideRef.current.slideIndex === slideIndex) {
          const { startSentenceIdx } = pendingAudioSlideRef.current;
          pendingAudioSlideRef.current = null;
          playSlideAudioRef.current(slideIndex, startSentenceIdx);
        } else if (waitingForSvgSlideIdxRef.current === slideIndex || currentSlideRef.current === slideIndex || slideIndex === 0) {
          waitingForSvgSlideIdxRef.current = null;
          setIsStreamingLoading(false);
          setDisplayedMarkdown(slidesRef.current[slideIndex]?.markdown || '');
          setTimeout(() => {
            if (!isPlayingQueueRef.current) {
              processAudioQueueRef.current();
            }
          }, 40);
        }
      } else {
        throw new Error('Görsel bulunamadı');
      }
    } catch (err: any) {
      if (err?.name === 'AbortError') {
        console.warn(`[CinAliPlayer] Slayt ${slideIndex} için SVG çizim isteği zaman aşımına (45sn) uğradı.`);
      } else {
        console.warn(`[CinAliPlayer] Slayt ${slideIndex} için SVG fallback uygulandı:`, err);
      }

      // Slayta ÖZEL, dinamik ve konuyu birebir yansıtan eğitici SVG üret (Asla her slaytta aynı görsel kalmaz!)
      const fallbackSvg = createDynamicSlideSvg(
        slideIndex,
        slideHeading,
        targetSlide?.markdown || '',
        lesson.title
      );

      slideSvgsRef.current[slideIndex] = fallbackSvg;
      setSlideSvgs(prev => ({ ...prev, [slideIndex]: fallbackSvg }));

      setSlides(prev => {
        const updated = [...prev];
        if (updated[slideIndex]) {
          updated[slideIndex] = {
            ...updated[slideIndex],
            svgVisual: fallbackSvg,
            svgLoading: false,
          };
        }
        return updated;
      });

      // Bir sonraki slaytın görselini arka planda üretmeye devam et (zincir kopmasın)
      const nextSlideIdx = slideIndex + 1;
      setTimeout(() => {
        const nextSlide = slidesRef.current[nextSlideIdx];
        if (nextSlide && !nextSlide.svgVisual && !svgRequestsRef.current.has(nextSlideIdx)) {
          svgRequestsRef.current.add(nextSlideIdx);
          const nextHeadingMatch = nextSlide.markdown?.match(/##\s*([^\n]+)/);
          const nextHeading = nextHeadingMatch ? nextHeadingMatch[1].trim() : `${lesson.title} — Slayt ${nextSlideIdx + 1}`;
          const desc = nextSlide.visualDescription || `${nextHeading}. Bu konuyu anlatan eğitici infografik ve akış şeması.`;
          fetchSvgForSlide(nextSlideIdx, desc);
        }
      }, 150);

      if (pendingAudioSlideRef.current && pendingAudioSlideRef.current.slideIndex === slideIndex) {
        const { startSentenceIdx } = pendingAudioSlideRef.current;
        pendingAudioSlideRef.current = null;
        playSlideAudioRef.current(slideIndex, startSentenceIdx);
      } else if (waitingForSvgSlideIdxRef.current === slideIndex || currentSlideRef.current === slideIndex || slideIndex === 0) {
        waitingForSvgSlideIdxRef.current = null;
        setIsStreamingLoading(false);
        setDisplayedMarkdown(slidesRef.current[slideIndex]?.markdown || '');
        setTimeout(() => {
          if (!isPlayingQueueRef.current) {
            processAudioQueueRef.current();
          }
        }, 40);
      }
    } finally {
      clearTimeout(timeoutId);
      setSlides(prev => {
        if (!prev[slideIndex]?.svgLoading) return prev;
        const updated = [...prev];
        if (updated[slideIndex]) {
          updated[slideIndex] = {
            ...updated[slideIndex],
            svgLoading: false,
          };
        }
        return updated;
      });
    }
  }, [lesson.title]);

  useEffect(() => { slidesRef.current = slides; }, [slides]);
  useEffect(() => { currentSlideRef.current = currentSlide; }, [currentSlide]);

  // ── SVG Animasyon Tetikleyici (Next Tick & DOM Hazırlığı) ──────────
  const currentSvgForAnim = slides[currentSlide]?.svgVisual || slideSvgs[currentSlide] || undefined;
  useEffect(() => {
    // Önceki animasyonları temizle
    if (svgAnimCleanupRef.current) {
      svgAnimCleanupRef.current();
      svgAnimCleanupRef.current = undefined;
    }

    // SVG varsa ve container hazırsa animasyonu başlat
    if (!currentSvgForAnim || !svgVisualRef.current) return;

    // 1. Çizgi ve okların ilk boyamada parlamasını önlemek için hazırlık sınıfını hemen ekle
    svgVisualRef.current.classList.add('svg-anim-ready');

    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    // 2. Tarayıcının SVG'yi DOM'a tam olarak çizmesini ve BBox boyutlarını
    //    doğru hesaplamasını beklemek için Next Tick (rAF + mikro 50ms timeout)
    const animationFrameId = requestAnimationFrame(() => {
      timeoutId = setTimeout(() => {
        if (svgVisualRef.current) {
          svgAnimCleanupRef.current = animateSvg(svgVisualRef.current);
        }
      }, 50);
    });

    return () => {
      cancelAnimationFrame(animationFrameId);
      if (timeoutId) clearTimeout(timeoutId);
      if (svgAnimCleanupRef.current) {
        svgAnimCleanupRef.current();
        svgAnimCleanupRef.current = undefined;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSlide, currentSvgForAnim]);

  // Herhangi bir slayta geçildiğinde veya slayt açıldığında görsel eksikse hemen anında üret
  useEffect(() => {
    if (slides.length === 0) return;
    const active = slides[currentSlide];
    if (active && !active.svgVisual && !svgRequestsRef.current.has(currentSlide)) {
      svgRequestsRef.current.add(currentSlide);
      const nextHeadingMatch = active.markdown?.match(/##\s*([^\n]+)/);
      const slideHeading = nextHeadingMatch ? nextHeadingMatch[1].trim() : `${lesson.title} — Slayt ${currentSlide + 1}`;
      const cleanBody = (active.markdown || '').replace(/##[^\n]+/g, '').replace(/[*_#`]/g, '').trim();
      const firstSentence = cleanBody.split(/[.!?]/)[0]?.trim() || '';
      const desc = active.visualDescription || `${slideHeading}: ${firstSentence}. Bu konuyu anlatan detaylı eğitici infografik ve akış şeması.`;
      setSlides(prev => {
        const u = [...prev];
        if (u[currentSlide]) u[currentSlide] = { ...u[currentSlide], svgLoading: true };
        return u;
      });
      fetchSvgForSlide(currentSlide, desc);
    }
  }, [currentSlide, slides, fetchSvgForSlide, lesson.title]);

  useEffect(() => {
    lessonAutoFinishedRef.current = false;
    speechQueueManagerRef.current.clearCache();
  }, [lesson.id]);

  const aiMessages    = messages.filter(m => m.sender === 'ai' && m.id !== 'welcome');
  const lastAiMessage = aiMessages[aiMessages.length - 1];

  // ═══════════════════════════════════════════════════════════════════════
  //  TTS
  // ═══════════════════════════════════════════════════════════════════════
  const speakText = useCallback((text: string, onComplete: () => void) => {
    isInterruptedRef.current = false;
    speechQueueManagerRef.current.speak(text, {
      onStart:    () => startSpeaking(),
      onBoundary: () => onBoundary(),
      onComplete: () => { if (!isInterruptedRef.current) { stopSpeaking(); onComplete(); } },
    });
  }, [onBoundary, startSpeaking, stopSpeaking]);

  const playBase64Speech = useCallback((base64: string, onComplete: () => void) => {
    isInterruptedRef.current = false;
    if (currentAudioRef.current) { currentAudioRef.current.pause(); currentAudioRef.current = null; }
    const audio = new Audio(`data:audio/mp3;base64,${base64}`);
    currentAudioRef.current = audio;
    audio.onplay   = () => startSpeaking();
    audio.onpause  = () => stopSpeaking();
    audio.onended  = () => { stopSpeaking(); onComplete(); };
    audio.onerror  = () => { stopSpeaking(); onComplete(); };
    audio.play().catch(err => {
      if (err.name !== 'AbortError') { stopSpeaking(); onComplete(); }
    });
  }, [startSpeaking, stopSpeaking]);

  const interruptTTS = useCallback(() => {
    isInterruptedRef.current = true;
    setIsPaused(false);
    if (currentAudioRef.current) { currentAudioRef.current.pause(); currentAudioRef.current = null; }
    speechQueueManagerRef.current.stop();
    stopSpeaking();
  }, [stopSpeaking]);

  // ═══════════════════════════════════════════════════════════════════════
  //  SES & GÖRSEL TAM EŞZAMANLILIK KUYRUĞU
  // ═══════════════════════════════════════════════════════════════════════
  const processAudioQueue = useCallback(() => {
    if (isInterruptedRef.current) return;

    if (audioQueueRef.current.length === 0) {
      isPlayingQueueRef.current = false;
      return;
    }

    const nextItem = audioQueueRef.current[0];

    // Bu cümlenin ait olduğu slaytı bul
    let matchingSlideIdx = -1;
    for (let i = 0; i < slidesRef.current.length; i++) {
      if (slidesRef.current[i].sentences?.some(s => s.text === nextItem.text)) {
        matchingSlideIdx = i;
        break;
      }
    }

    // ⚠️ GÖRSEL ÜRETİLMEDEN ASLA SESLİ ANLATIMA GEÇME!
    if (matchingSlideIdx !== -1) {
      const targetSlide = slidesRef.current[matchingSlideIdx];
      const hasSvg = !!targetSlide?.svgVisual || !!slideSvgsRef.current[matchingSlideIdx];
      if (!hasSvg) {
        waitingForSvgSlideIdxRef.current = matchingSlideIdx;
        isPlayingQueueRef.current = false;
        if (matchingSlideIdx !== currentSlideRef.current) {
          setCurrentSlide(matchingSlideIdx);
          currentSlideRef.current = matchingSlideIdx;
        }
        return;
      }
    }

    const item = audioQueueRef.current.shift()!;
    isPlayingQueueRef.current = true;
    waitingForSvgSlideIdxRef.current = null;
    setIsStreamingLoading(false);

    if (matchingSlideIdx !== -1 && matchingSlideIdx !== currentSlideRef.current) {
      setSlideTransition('out');
      setTimeout(() => {
        setCurrentSlide(matchingSlideIdx);
        currentSlideRef.current = matchingSlideIdx;
        setSlideTransition('in');
        setTimeout(() => {
          setSlideTransition('none');
          const slide = slidesRef.current[matchingSlideIdx];
          if (slide) setDisplayedMarkdown(slide.markdown);
        }, 300);
      }, 200);
    } else if (matchingSlideIdx !== -1) {
      setDisplayedMarkdown(slidesRef.current[matchingSlideIdx].markdown);
    }

    setMode('lesson');
    if (item.audioBase64) {
      playBase64Speech(item.audioBase64, () => processAudioQueue());
    } else {
      processAudioQueue();
    }
  }, [playBase64Speech]);

  useEffect(() => {
    processAudioQueueRef.current = processAudioQueue;
  }, [processAudioQueue]);

  const playSlideAudio = useCallback((slideIndex: number, startSentenceIdx = 0) => {
    const slide = slidesRef.current[slideIndex];
    if (!slide?.sentences) return;
    interruptTTS();

    const hasSvg = !!slide?.svgVisual || !!slideSvgsRef.current[slideIndex];
    if (!hasSvg) {
      pendingAudioSlideRef.current = { slideIndex, startSentenceIdx };
      return;
    }
    pendingAudioSlideRef.current = null;

    setMode('lesson');
    currentSentenceIdxRef.current = startSentenceIdx;

    const playNext = () => {
      if (currentSentenceIdxRef.current >= slide.sentences!.length) {
        setMode('idle');
        const isLast = slideIndex === slidesRef.current.length - 1;
        if (isLast && !lessonAutoFinishedRef.current && !isLessonCompleted) {
          lessonAutoFinishedRef.current = true;
          onCompleteLesson?.();
        }
        return;
      }
      const s = slide.sentences![currentSentenceIdxRef.current++];
      if (s.audioBase64) { playBase64Speech(s.audioBase64, playNext); }
      else { playNext(); }
    };
    playNext();
  }, [playBase64Speech, interruptTTS, onCompleteLesson, isLessonCompleted]);

  useEffect(() => {
    playSlideAudioRef.current = playSlideAudio;
  }, [playSlideAudio]);

  const transitionToSlide = useCallback((targetIndex: number, thenSpeak: boolean, startSentenceIdx = 0) => {
    setSlideTransition('out');
    setDisplayedMarkdown('');
    setTimeout(() => {
      setCurrentSlide(targetIndex);
      currentSlideRef.current = targetIndex;
      setSlideTransition('in');
      setTimeout(() => {
        setSlideTransition('none');
        const slide = slidesRef.current[targetIndex];
        if (slide) {
          setDisplayedMarkdown(slide.markdown);
          if (thenSpeak) playSlideAudio(targetIndex, startSentenceIdx);
        }
      }, 400);
    }, 300);
  }, [playSlideAudio]);

  // ── Son AI mesajını yakala (offline mod) ──────────────────────────────
  useEffect(() => {
    if (!lastAiMessage) return;
    const displayText = lastAiMessage.text;
    const audioText   = lastAiMessage.audioText || lastAiMessage.text;

    if (isRequestingLesson) {
      if (processedMessageIdRef.current === lastAiMessage.id) return;
      processedMessageIdRef.current = lastAiMessage.id;
      setIsRequestingLesson(false);
      setMode('lesson');
      const newSlides = splitIntoSlides(displayText, audioText);
      setSlides(newSlides);
      slidesRef.current = newSlides;
      setCurrentSlide(0); currentSlideRef.current = 0;
      setDisplayedMarkdown('');
      setSlideTransition('in');

      // Offline modda tüm görsel isteklerini hemen başlat
      svgRequestsRef.current = new Set();
      newSlides.forEach((slide, idx) => {
        if (slide.visualDescription && !slide.svgVisual) {
          svgRequestsRef.current.add(idx);
          slide.svgLoading = true;
          fetchSvgForSlide(idx, slide.visualDescription);
        }
      });

      setTimeout(() => {
        setSlideTransition('none');
        if (newSlides.length > 0) {
          setDisplayedMarkdown(newSlides[0].markdown);
          const needsSvg = !!newSlides[0].visualDescription && !newSlides[0].svgVisual;
          if (!needsSvg) {
            speakText(newSlides[0].speechText, () => {
              setMode('idle');
              if (newSlides.length === 1 && !lessonAutoFinishedRef.current && !isLessonCompleted) {
                lessonAutoFinishedRef.current = true;
                onCompleteLesson?.();
              }
            });
          }
        }
      }, 500);
      return;
    }

    if (qaWaitingAI) {
      setQaMessages(prev => {
        const idx = prev.findIndex(m => m.id === lastAiMessage.id);
        if (idx >= 0) { const a = [...prev]; a[idx] = { ...a[idx], text: displayText }; return a; }
        return [...prev, { id: lastAiMessage.id, role: 'teacher', text: displayText }];
      });
      if (!isStreaming && !isGlobalAudioPlaying && !isLoading && displayText) {
        setQaWaitingAI(false);
        setQaAnswerDone(true);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastAiMessage, isLoading, isStreaming, isGlobalAudioPlaying]);

  // ═══════════════════════════════════════════════════════════════════════
  //  DERS BAŞLAT — Stream modu
  // ═══════════════════════════════════════════════════════════════════════
  const handleStartLesson = async () => {
    if (isRequestingLesson || isLoading || isStreamingLoading || isQuotaExceeded) return;
    isInterruptedRef.current = false;

    setIsRequestingLesson(true);
    setIsStreamingLoading(true);
    setSlides([]); slidesRef.current = [];
    setCurrentSlide(0); currentSlideRef.current = 0;
    setDisplayedMarkdown('');
    setMode('lesson');

    const title = lesson.title || '';
    const baseTextForLength = `Sen görsel bir ders tasarımcısısın. "${title}" konusunu çocuklara GÖRSEL ÜZERİNDEN anlat. KESİNLİKLE selamlama yok — doğrudan ilk slayt başlığıyla başla.

Derslerin anlatımı klasik "giriş, gelişme, sonuç" gibi kompozisyon yapıları ve şablonları kullanma.
Ayrıca metin içerisinde KESİNLİKLE "1.", "2.", "3.", "Giriş:", "Gelişme:", "Sonuç:" gibi başlıklar, alt başlıklar veya numaralandırmalar YAZMA.

════════════════════════════════════════
ZORUNLU SLAYT FORMATI (her slayt bu düzende):
════════════════════════════════════════

## [N]. [Slayt Başlığı]

[GÖRSEL_TANIMI: Bu slayt için çizilecek görselin son derece detaylı açıklaması. Çizilecek öğeler, renkleri, konumları ve ok akışları burada belirtilmelidir. Çocuklar için anlaşılır, neon renk paletine (Mor, Yeşil, Turuncu, Mavi) ve SVG kalitesine uygun bir çizim açıklaması olmalıdır.]

[AKICI ANLATIM PARAGRAFLARI — BAŞLIKSIZ, ETİKETSİZ]

════════════════════════════════════════
GÖRSEL VE SVG STANDARTLARI:
════════════════════════════════════════
- Görsel tanımları [GÖRSEL_TANIMI: ...] formatında olmalıdır.
- Bu açıklamada çizilecek öğelerin yerleri, şekilleri, simetrisi ve neon renkleri (#D946EF mor, #22C55E yeşil, #FF5722 turuncu, #06B6D4 mavi gibi) son derece detaylı tarif edilmelidir.
- EĞER KARŞILAŞTIRMA (VS) YAPILIYORSA: Sol kutu ve Sağ kutuda nelerin çizileceğini (şekiller, sayılar, hücreler, etiketler) ayrı ayrı EKSİKSİZ belirt (Asla sağ kutuyu boş bırakma!).
- EĞER AŞAMALI SÜREÇ ANLATILIYORSA: 1. Adım, 2. Adım ve 3. Adımın (sonuç durumunun) herbirinde ne çizileceğini sırasıyla eksiksiz tarif et (Asla son adımı boş bırakma!).
- Sadece akıcı ders anlatım paragrafları seslendirileceği için, görsel kodları yerine sadece açıklaması bu etiketin içinde yazılmalıdır.
- Çizimler 8-20 arasında odaklanmış ve renkli element içermelidir.

KONU TİPİNE GÖRE GÖRSEL:
🔬 Bilim/Biyoloji → anatomik diyagram, bölünme aşamaları, ok akışları, kesit görünüm
🖥️ Programlama → bellek kutuları, akış şeması (elmas=karar, dikdörtgen=işlem)
📐 Matematik → geometrik şekiller, koordinat sistemi, sayı doğrusu

════════════════════════════════════════
METİN ANLATIM VE GÖRSEL REHBERLİK KURALLARI:
════════════════════════════════════════
- Anlatım metni KESİNLİKLE yukarıdaki görseli/diyagramı rehber edinerek açıklamalıdır!
  * Örneğin: "Diyagramımızın sol tarafında mavi kutuda gördüğümüz...", "Ortadaki yeşil alanda yer alan...", "Sağdaki mor kutuda gösterilen..." şeklinde görseldeki her unsuru işaret ederek açıkla.
- KESİNLİKLE "Giriş:", "Gelişme:", "Sonuç:", "Örnek:", "Örnek 1:", "Örnek 2:" gibi mekanik kompozisyon etiketleri YAZMA. Bunlar KESİNLİKLE YASAKTIR.
- Metin 2-3 ferah, akıcı, okunabilirliği yüksek ve çocukların kolayca anlayacağı doğal konuşma paragraflarından oluşmalıdır.
- Önemli kavramlar ve görseldeki anahtar terimler **kalın** yazılmalıdır.

SLAYT SAYISI: KESİNLİKLE MİNİMUM 6 SLAYT (her birinde görsel tanımı + anlatım).`;

    const customPromptHeader = `\n\nÖNEMLİ NOT: Aşağıdaki ek yönergeler içerik ve yaş seviyesiyle ilgilidir. Teknik slayt yapısı ve SVG kuralları için yukarıdaki formatı birinci öncelikle uygula.\n\nEk Eğitmen Yönergesi: `;

    const maxPromptLength = 15000;
    const remainingBudget = maxPromptLength - baseTextForLength.length - customPromptHeader.length;
    let truncatedContent = (lesson.content || '')
      .replace(/(?:çıktı\s+)?formatı(?:n\s+şu\s+şekilde\s+olmalı|nız)[\s\S]*/gi, '')
      .replace(/(?:örnek\s+)?çıktı\s+şablonu[\s\S]*/gi, '')
      .trim();
    if (truncatedContent && remainingBudget > 10 && truncatedContent.length > remainingBudget) {
      truncatedContent = truncatedContent.substring(0, remainingBudget - 50) + '... (kısaltıldı)';
    }
    const prompt = baseTextForLength + (truncatedContent ? `${customPromptHeader}${truncatedContent}` : '');

    try {
      const token = await authService.getAccessToken();
      if (!token) throw new Error('Oturum bulunamadı.');

      const response = await fetch(`${API_BASE}/api/ai/stream-chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          message: prompt,
          history: [],
          model: 'gemini-2.0-flash',
          studentContext,
          isLessonRequest: true,
        }),
      });

      if (!response.ok) throw new Error('Ders başlatılamadı.');
      const reader = response.body?.getReader();
      if (!reader) throw new Error('Stream başlatılamadı.');

      const decoder = new TextDecoder();
      let sseBuffer = '';
      const flatSentences: { text: string; audioBase64: string }[] = [];
      audioQueueRef.current = [];
      isPlayingQueueRef.current = false;
      streamActiveRef.current = true;
      setIsRequestingLesson(false);

      svgRequestsRef.current = new Set();

      let streamDone = false;
      while (!streamDone) {
        const { done, value } = await reader.read();
        if (value) sseBuffer += decoder.decode(value, { stream: true });

        const lines = sseBuffer.split('\n');
        if (done) { sseBuffer = ''; }
        else { sseBuffer = lines.pop() || ''; }

        for (const line of lines) {
          const trimmed = line.trim();
          if (!trimmed.startsWith('data:')) continue;
          const jsonStr = trimmed.substring(5).trim();
          try {
            const data = JSON.parse(jsonStr);
            if (data.isFinished) {
              // Akış bitti — geçersiz slaytları temizle
              const validSlides = (slidesRef.current || []).filter(slide => {
                const text = (slide.markdown || '').replace(/^\s*##.*$/gm, '').trim();
                return text.length > 0 || !!slide.svgVisual || !!slide.visualDescription;
              });
              if (validSlides.length > 0) {
                setSlides(validSlides);
                slidesRef.current = validSlides;
                if (currentSlideRef.current >= validSlides.length) {
                  const last = validSlides.length - 1;
                  setCurrentSlide(last);
                  currentSlideRef.current = last;
                }
                const validTexts = new Set(validSlides.flatMap(s => s.sentences || []).map(s => s.text));
                audioQueueRef.current = audioQueueRef.current.filter(i => validTexts.has(i.text));

                // 1. slaytın görseli başlatılmadıysa hemen başlat
                if (!svgRequestsRef.current.has(0)) {
                  svgRequestsRef.current.add(0);
                  const desc = validSlides[0]?.visualDescription || `${lesson.title} - 1. Slayt Kavram Görseli`;
                  fetchSvgForSlide(0, desc);
                }
              }
              streamDone = true;
              break;
            }

            if (data.text) {
              const sentenceText  = data.text as string;
              const audioBase64   = data.audioBase64 as string;

              flatSentences.push({ text: sentenceText, audioBase64 });
              audioQueueRef.current.push({ text: sentenceText, audioBase64 });

              // Slaytları yeniden oluştur
              const newSlides: Slide[] = [];
              let curText = '';
              let curSentences: { text: string; audioBase64: string }[] = [];

              for (const s of flatSentences) {
                const startsWithHeader = /^\s*##+/m.test(s.text);
                if (startsWithHeader && curText.trim()) {
                  const idx = newSlides.length;
                  const prevSlide = slidesRef.current[idx];
                  const { svg, visualDescription, cleanMarkdown } = extractVisualAndSvg(curText.trim(), lesson.title);
                  newSlides.push({
                    markdown: cleanMarkdown,
                    svgVisual: svg || prevSlide?.svgVisual || slideSvgsRef.current[idx],
                    visualDescription: visualDescription,
                    speechText: curSentences.map(x => x.text).join(' '),
                    sentences: curSentences,
                    svgLoading: prevSlide ? prevSlide.svgLoading : false,
                  });
                  curText = '';
                  curSentences = [];
                }
                curText += (!curText ? '' : (/^\s/.test(s.text) ? '' : ' ')) + s.text;
                curSentences.push(s);
              }

              if (curText.trim()) {
                const idx = newSlides.length;
                const prevSlide = slidesRef.current[idx];
                const { svg, visualDescription, cleanMarkdown } = extractVisualAndSvg(curText.trim(), lesson.title);
                newSlides.push({
                  markdown: cleanMarkdown,
                  svgVisual: svg || prevSlide?.svgVisual || slideSvgsRef.current[idx],
                  visualDescription: visualDescription,
                  speechText: curSentences.map(x => x.text).join(' '),
                  sentences: curSentences,
                  svgLoading: prevSlide ? prevSlide.svgLoading : false,
                });
              }

              setSlides(newSlides);
              slidesRef.current = newSlides;

              // 1. Slayt oluştuğu an SADECE 1. slaytın görsel üretimini başlat (sıralı arka plan kuyruğu devreye girer)
              if (newSlides.length > 0 && !svgRequestsRef.current.has(0)) {
                svgRequestsRef.current.add(0);
                const firstDesc = newSlides[0].visualDescription || `${lesson.title} - 1. Slayt Görseli`;
                fetchSvgForSlide(0, firstDesc);
              }

              // Ses kuyruğunu tetikle — processAudioQueue görsel hazır olduğunda ses, metin ve görüntüyü aynı anda başlatır
              if (!isPlayingQueueRef.current) {
                processAudioQueue();
              }
            }
          } catch (_) { /* ignore */ }
        }
        if (done) break;
      }

      streamActiveRef.current = false;
      setIsStreamingLoading(false);
      if (!isPlayingQueueRef.current) processAudioQueue();

    } catch (err: any) {
      console.error('Ders stream hatası:', err);
      setIsRequestingLesson(false);
      setIsStreamingLoading(false);
      streamActiveRef.current = false;
      alert(err?.message || 'Ders yüklenirken bir hata oluştu.');
    }
  };

  const handleDownloadPDF = async () => {
    if (slides.length === 0) { alert('Ders içeriği henüz yüklenmedi.'); return; }
    try {
      const content = `# ${course.title} - ${lesson.title}\n\n${slides.map((s, i) => `## Bölüm ${i+1}\n\n${s.markdown}`).join('\n\n')}`;
      await coursesService.saveNote({ courseId: course.id, lessonId: lesson.id, pdfNote: content });
      alert("Ders özeti 'Notlarım' sayfasına kaydedildi!");
    } catch (e: any) { alert(e?.message || 'Hata oluştu.'); }
  };

  // ── Q&A ──────────────────────────────────────────────────────────────
  const submitQuestion = (questionText: string) => {
    if (!questionText.trim() || isLoading || isQuotaExceeded) return;
    if (!qaOpen) {
      interruptTTS();
      resumeSlideIdxRef.current     = currentSlideRef.current;
      resumeSentenceIdxRef.current  = Math.max(0, currentSentenceIdxRef.current - 1);
      setMode('qa'); setQaOpen(true); setQaMessages([]); setQaAnswerDone(false);
    }
    setQaMessages(prev => [...prev, { id: qaId(), role: 'student', text: questionText }]);
    setQaWaitingAI(true); setQaAnswerDone(false);
    const ctx = slidesRef.current[currentSlideRef.current]?.markdown || '';
    sendMessage(`[BAĞLAM: "${lesson.title}" dersinin ${currentSlideRef.current+1}. slaytı: "${ctx.substring(0,500)}"]\n\nÖğrenci sorusu: ${questionText}\n\n(Kısa, anlaşılır, Türkçe cevap ver.)`);
    setChatInput('');
  };

  const handleAskQuestion  = (e: React.FormEvent) => { e.preventDefault(); submitQuestion(chatInput); };

  const handleUnderstood = () => {
    interruptTTS(); stopAudio();
    setQaOpen(false); setQaMessages([]); setQaAnswerDone(false); setMode('lesson');
    setTimeout(() => transitionToSlide(resumeSlideIdxRef.current, true, resumeSentenceIdxRef.current), 100);
  };

  const handleNotUnderstood = () => {
    if (isLoading || isQuotaExceeded) return;
    interruptTTS(); setQaAnswerDone(false); setQaWaitingAI(true);
    const msg: QAMessage = { id: qaId(), role: 'student', text: '❌ Anlamadım, biraz daha açıklar mısın?' };
    setQaMessages(prev => [...prev, msg]);
    const ctx = slidesRef.current[currentSlideRef.current]?.markdown || '';
    sendMessage(`[BAĞLAM: "${lesson.title}" dersinin ${currentSlideRef.current+1}. slaytı: "${ctx.substring(0,300)}"]\nÖğrenci anlamadı. Daha basit, günlük hayattan örneklerle tekrar açıkla.`);
  };

  const goToSlide = (idx: number) => {
    if (idx < 0 || idx >= slides.length) return;
    // Mevcut slaytın sesli anlatımı devam ederken ileri atlamayı engelle
    if (idx > currentSlideRef.current && (isSpeaking || isPlayingQueueRef.current)) {
      return;
    }
    interruptTTS();
    setDisplayedMarkdown('');
    setTimeout(() => transitionToSlide(idx, true), 50);
  };

  // ── Cleanup ───────────────────────────────────────────────────────────
  useEffect(() => {
    speechQueueManagerRef.current.registerAsActive();
    return () => {
      streamActiveRef.current  = false;
      isInterruptedRef.current = true;
      if (currentAudioRef.current) {
        currentAudioRef.current.pause();
        currentAudioRef.current.onended = null;
        currentAudioRef.current.onerror = null;
        currentAudioRef.current = null;
      }
      speechQueueManagerRef.current.stop();
      speechQueueManagerRef.current.clearCache();
      stopAudio(); stopGlobalTTS();
      try { window.speechSynthesis.cancel(); } catch { /* ignore */ }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleFullscreen = () => {
    if (!isFullscreen) {
      setIsFullscreen(true);
      playerContainerRef.current?.requestFullscreen?.().catch(() => {});
    } else {
      setIsFullscreen(false);
      document.fullscreenElement && document.exitFullscreen?.().catch(() => {});
    }
  };

  const handleExitLesson = () => {
    const isLast = currentSlideRef.current === slidesRef.current.length - 1 && slidesRef.current.length > 0;
    streamActiveRef.current = false;
    interruptTTS(); stopAudio();
    setSlides([]); setCurrentSlide(0); setDisplayedMarkdown(''); setMode('idle');
    if (isLast && !lessonAutoFinishedRef.current && !isLessonCompleted) {
      lessonAutoFinishedRef.current = true; onCompleteLesson?.();
    }
    navigate(`/courses/${course.id}`);
  };

  const togglePause = () => {
    if (isPaused) {
      currentAudioRef.current?.play().catch(() => {});
      speechQueueManagerRef.current.resume();
      setIsPaused(false);
    } else {
      currentAudioRef.current?.pause();
      speechQueueManagerRef.current.pause();
      setIsPaused(true);
    }
  };

  const changeSpeed = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const r = parseFloat(e.target.value);
    setPlaybackRate(r);
    speechQueueManagerRef.current.setRate(r);
  };

  // ── Render ────────────────────────────────────────────────────────────
  const mouthPath  = getMouthPath(mouthOpenness);
  const eyeRadius  = isSpeaking ? 3.3 : 3;

  // Tek kaynak — slides state'den oku
  const activeSlide      = slides.length > 0 && currentSlide < slides.length ? slides[currentSlide] : null;
  const currentSvgRaw    = activeSlide?.svgVisual || slideSvgs[currentSlide] || slideSvgsRef.current[currentSlide];
  const hasSlideSvg      = !!currentSvgRaw;
  const isWaitingSvg     = !!activeSlide && !hasSlideSvg && (activeSlide.svgLoading || !!activeSlide.visualDescription || isStreamingLoading);

  // Görsel henüz hazır değilse metni gizle — görsel, metin ve ses aynı anda açılsın!
  const displayMarkdown  = isWaitingSvg ? '' : (displayedMarkdown || activeSlide?.markdown || '');

  let currentSvgVisual: string | undefined = undefined;
  if (currentSvgRaw) {
    currentSvgVisual = adaptSvgToLightSharpTheme(currentSvgRaw);
  }

  const totalSlides    = slides.length;
  const hasLesson      = totalSlides > 0;
  const progressPercent = totalSlides > 0 ? ((currentSlide + 1) / totalSlides) * 100 : 0;
  const slideAnimClass = slideTransition === 'out' ? 'slide-fade-out' : slideTransition === 'in' ? 'slide-fade-in' : '';

  return (
    <div className={`new-lesson-grid-wrapper ${isFullscreen ? 'fullscreen-active' : ''}`}>
      <div className="lesson-left-layout-column">
        <div ref={playerContainerRef} className={`cin-ali-player-container ${isFullscreen ? 'board-fullscreen' : ''}`}>
          <div className="youtube-style-board">

            {/* Robot Avatar */}
            <div className={`board-avatar-side-panel ${isSpeaking ? 'is-speaking-active' : ''}`}>
              <div className="robot-avatar-container">
                <div className="robot-head">
                  <div className="robot-antenna"><div className="antenna-bulb" /></div>
                  <div className="robot-screen">
                    <div className="robot-eye left" />
                    <div className="robot-eye right" />
                  </div>
                </div>
                <div className="robot-body">
                  <div className="robot-chest"><div className="chest-heart" /></div>
                  <div className="robot-arm left" />
                  <div className="robot-arm right" />
                </div>
              </div>
              <div className="robot-sound-waves">
                {[0,1,2,3,4].map(i => <div key={i} className="wave-bar" />)}
              </div>
              <span className="robot-name-badge">CİNALİ - AI HOCA</span>
            </div>

            {/* Slayt İçeriği */}
            <div className="board-slide-content-area">
              {hasLesson && (
                <div className="slide-content-header-slug">
                  • SLAYT {currentSlide + 1} / {totalSlides} — {lesson.title.toUpperCase()}
                </div>
              )}
              <div
                ref={boardTextAreaRef}
                className={`board-text-area markdown-body ${slideAnimClass}`}
                style={{ flex: '1 1 0%', height: 0, overflowY: 'auto', overscrollBehaviorY: 'contain', WebkitOverflowScrolling: 'touch', padding: '16px 28px 28px 28px' }}
              >
                {hasLesson ? (
                  isWaitingSvg ? (
                    <div className="slide-preparing-container" style={{
                      minHeight: 260,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexDirection: 'column',
                      gap: 14,
                      background: '#F8FAFC',
                      borderRadius: 0,
                      border: '1px dashed #CBD5E1',
                      padding: 28,
                      margin: '16px auto',
                      width: '100%',
                      textAlign: 'center'
                    }}>
                      <div className="animate-bounce" style={{ fontSize: '2.4rem' }}>🎨</div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <h4 style={{ color: '#0284C7', fontSize: '1.05rem', fontWeight: 700, margin: 0 }}>
                          Ders Anlatımı ve Görseli Hazırlanıyor...
                        </h4>
                        <p style={{ color: '#64748B', fontSize: '0.82rem', margin: 0 }}>
                          Görsel çizimi tamamlandığında anlatım ve ses birlikte başlayacak.
                        </p>
                      </div>
                      <div className="animate-pulse" style={{ display: 'flex', gap: 6, marginTop: 4 }}>
                        <div style={{ width: 8, height: 8, borderRadius: 0, background: '#0284C7' }} />
                        <div style={{ width: 8, height: 8, borderRadius: 0, background: '#22C55E' }} />
                        <div style={{ width: 8, height: 8, borderRadius: 0, background: '#FF5722' }} />
                      </div>
                    </div>
                  ) : (
                    (displayMarkdown || currentSvgVisual) ? (
                      <>
                        {/* SVG alanı — varsa göster, yoksa fallback */}
                        {currentSvgVisual ? (
                          <div
                            ref={svgVisualRef}
                            className="slide-svg-visual"
                            dangerouslySetInnerHTML={{ __html: currentSvgVisual }}
                          />
                        ) : (
                          <div className="slide-svg-visual fallback-concept-visual">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 560 140" style={{ width: '100%', height: 'auto', borderRadius: 0 }}>
                              <rect width="560" height="140" fill="#FFFFFF" rx="0" stroke="#CBD5E1" strokeWidth="1" />
                              <circle cx="50" cy="70" r="26" fill="rgba(2,132,199,0.1)" stroke="#0284C7" strokeWidth="2" />
                              <text x="50" y="76" fill="#0284C7" fontSize="20" textAnchor="middle" fontWeight="bold">💡</text>
                              <rect x="95" y="42" width="435" height="26" fill="#F8FAFC" rx="0" stroke="#E2E8F0" strokeWidth="1" />
                              <text x="105" y="60" fill="#0F172A" fontSize="13" fontWeight="bold" fontFamily="sans-serif">{lesson.title}</text>
                              <text x="95" y="95" fill="#64748B" fontSize="11" fontFamily="sans-serif">Konu anlatımı aşağıda devam ediyor.</text>
                            </svg>
                          </div>
                        )}

                        {/* Metin */}
                        {displayMarkdown && (
                          <div className="slide-text-content">
                            <ReactMarkdown
                              key={`${currentSlide}-${displayMarkdown.length}`}
                              components={{
                                p: ({ children }) => {
                                  const text = typeof children === 'string' ? children
                                    : Array.isArray(children) ? children.map(c => typeof c === 'string' ? c : '').join('') : '';
                                  return /^örnek/i.test(text.trim())
                                    ? <p className="markdown-example-block">{children}</p>
                                    : <p>{children}</p>;
                                },
                                code: ({ node, inline, className, children, ...props }: any) => {
                                  const match = /language-(\w+)/.exec(className || '');
                                  const lang = match?.[1] || '';
                                  if (!inline && lang) {
                                    return (
                                      <div className="terminal-code-container">
                                        <div className="terminal-header">
                                          <div className="terminal-dots">
                                            <span className="dot red" /><span className="dot yellow" /><span className="dot green" />
                                          </div>
                                          <span className="terminal-lang">{lang.toUpperCase()}</span>
                                        </div>
                                        <pre className={className} {...props}><code>{children}</code></pre>
                                      </div>
                                    );
                                  }
                                  return <code className={className} {...props}>{children}</code>;
                                }
                              }}
                            >{displayMarkdown}</ReactMarkdown>
                          </div>
                        )}
                      </>
                    ) : null
                  )
                ) : (
                  <div className="board-welcome-screen">
                    <h2>{lesson.title}</h2>
                    <p>Derse başlamak için hazır mısın?</p>
                  </div>
                )}
              </div>
            </div>

            {/* Başlatma overlay */}
            {!hasLesson && !isLoading && !isStreamingLoading && !isRequestingLesson && (
              <div className="lesson-start-overlay" onClick={handleStartLesson}>
                <button className="lesson-start-btn" style={{ borderRadius: 0 }}>
                  <span className="play-icon">▶</span> Dersi Başlat
                </button>
              </div>
            )}
            {(isLoading || isStreamingLoading) && !hasLesson && (
              <div className="cin-ali-loading" style={{ borderRadius: 0, background: '#0F172A' }}>Cin Ali düşünüyor...</div>
            )}
          </div>

          {/* Navigasyon footer */}
          {hasLesson && (
            <div className="board-navigation-footer">
              <button className="footer-nav-btn prev" onClick={() => goToSlide(currentSlide - 1)} disabled={currentSlide === 0}>← Önceki</button>

              <div className="footer-dots-indicators">
                {slides.map((_, i) => {
                  const isLocked = i > currentSlide && (isSpeaking || isPlayingQueueRef.current);
                  return (
                    <button
                      key={i}
                      className={`footer-dot ${i === currentSlide ? 'active' : ''}`}
                      onClick={() => !isLocked && goToSlide(i)}
                      title={isLocked ? `Slayt ${i + 1} (Önce mevcut anlatımı dinleyin)` : `Slayt ${i + 1}`}
                      style={{
                        cursor: isLocked ? 'not-allowed' : 'pointer',
                        opacity: isLocked ? 0.35 : 1,
                        borderRadius: 0,
                      }}
                    />
                  );
                })}
              </div>

              <div className="footer-playback-controls">
                <select className="speed-select" value={playbackRate} onChange={changeSpeed}
                  style={{ background: '#FFFFFF', color: '#0F172A', border: '1px solid #CBD5E1', borderRadius: 0, padding: '2px 6px', fontSize: 11 }}>
                  <option value={0.75}>0.75x</option>
                  <option value={1.0}>1.0x</option>
                  <option value={1.25}>1.25x</option>
                  <option value={1.5}>1.5x</option>
                </select>
                <button className="footer-control-icon-btn pause-play" onClick={togglePause} title="Durdur/Oynat" style={{ borderRadius: 0 }}>
                  {isPaused ? <Play size={13} fill="currentColor" /> : <Pause size={13} fill="currentColor" />}
                </button>
                <button className="footer-control-icon-btn fullscreen" onClick={toggleFullscreen} title="Tam Ekran" style={{ borderRadius: 0 }}>
                  {isFullscreen ? <Minimize size={13} /> : <Maximize size={13} />}
                </button>
              </div>

              {currentSlide < totalSlides - 1 || streamActiveRef.current ? (
                <button
                  className="footer-nav-btn next"
                  onClick={() => goToSlide(currentSlide + 1)}
                  disabled={isSpeaking || isPlayingQueueRef.current || currentSlide >= totalSlides - 1}
                  style={{
                    borderRadius: 0,
                    background: (isSpeaking || isPlayingQueueRef.current)
                      ? '#F1F5F9'
                      : 'linear-gradient(135deg, var(--teal,#17C79A), #059669)',
                    color: (isSpeaking || isPlayingQueueRef.current) ? '#94A3B8' : '#052E24',
                    border: (isSpeaking || isPlayingQueueRef.current) ? '1px solid #CBD5E1' : 'none',
                    fontWeight: 800,
                    cursor: (isSpeaking || isPlayingQueueRef.current) ? 'not-allowed' : 'pointer',
                    transition: 'all 0.25s ease',
                  }}
                >
                  {(isSpeaking || isPlayingQueueRef.current) ? '🔒 Dinleniyor...' : 'Sonraki →'}
                </button>
              ) : isLessonCompleted && nextLesson ? (
                <button className="footer-nav-btn next" onClick={() => navigate(`/lessons/${nextLesson.id}`)}
                  style={{ borderRadius: 0, background: 'linear-gradient(135deg,#6366f1,#4f46e5)', color: 'white', border: 'none', fontWeight: 'bold' }}>
                  Sonraki Ders ➡️
                </button>
              ) : (
                <button className="footer-nav-btn next complete-btn" onClick={onCompleteLesson} disabled={isLessonCompleted || isSpeaking || isPlayingQueueRef.current}
                  style={{ borderRadius: 0, background: isLessonCompleted ? '#DCFCE7' : 'linear-gradient(135deg,#10b981,#059669)', color: isLessonCompleted ? '#166534' : 'white', border: 'none', fontWeight: 'bold', cursor: isLessonCompleted ? 'default' : 'pointer' }}>
                  {isLessonCompleted ? 'Tamamlandı ✓' : 'Dersi Tamamla ✓'}
                </button>
              )}
            </div>
          )}
        </div>

        {/* Notlar */}
        {!isFullscreen && (
          <div className="card notes-sidebar-card" style={{ background: '#FFFFFF', border: '1.5px solid var(--border,#E2E8F0)', borderRadius: 0, boxShadow: '0 4px 20px rgba(15,23,42,0.03)' }}>
            <div className="notes-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h4 style={{ margin: 0, fontSize: 15, fontWeight: 800, color: 'var(--text-primary,#0F172A)' }}>📂 Notlarım</h4>
              <button className="notes-save-action-btn" onClick={onSaveNote} disabled={isSavingNote}
                style={{ background: 'linear-gradient(135deg,var(--teal),var(--teal-deep))', color: '#052E24', border: 'none', fontSize: 12, fontWeight: 800, padding: '6px 14px', borderRadius: 0, cursor: 'pointer' }}>
                {isSavingNote ? '...' : '💾 Kaydet'}
              </button>
            </div>
            <textarea value={notes} onChange={e => setNotes?.(e.target.value)}
              placeholder="Ders sırasında aklına gelen notları buraya kaydedebilirsin..."
              style={{ width: '100%', minHeight: 110, background: '#F8FAFC', border: '1.5px solid var(--border,#E2E8F0)', borderRadius: 0, color: 'var(--text-primary,#0F172A)', fontSize: 13.5, padding: 12, outline: 'none', resize: 'vertical', fontFamily: 'inherit' }} />
            {noteSaveStatus && (
              <span className={`notes-status-text ${noteSaveStatus.includes('hata') ? 'error' : ''}`}
                style={{ fontSize: 12, fontWeight: 700, color: noteSaveStatus.includes('hata') ? '#EF4444' : '#10B981', marginTop: 8, display: 'block' }}>
                {noteSaveStatus}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Sağ sütun */}
      {!isFullscreen && (
        <div className="lesson-right-layout-column">
          <div className="card new-ai-chat-sidebar-card">
            <div className="chat-header">
              <div className="chat-avatar-mini">🤖</div>
              <div className="chat-header-info">
                <h4>CinAli — AI Hoca</h4>
                <p>Anlık yardım için hazır</p>
              </div>
            </div>
            <div className="chat-quick-chips">
              {['Bu neden çalışır?','Daha basit anlat','Örnek ver','Mantığı ne?'].map(q => (
                <button key={q} type="button" onClick={() => submitQuestion(q)}>{q}</button>
              ))}
            </div>
            <div className="chat-messages-container">
              {qaMessages.length === 0 ? (
                <div className="empty-chat-welcome"><p>Dersle ilgili aklına takılanları sorabilirsin! 🎓</p></div>
              ) : qaMessages.map(msg => (
                <div key={msg.id} className={`chat-bubble-row ${msg.role}`}>
                  <div className="chat-bubble-avatar">{msg.role === 'student' ? '🧑‍💻' : '🤖'}</div>
                  <div className="chat-bubble-content">
                    <div className="chat-bubble-label">{msg.role === 'student' ? 'Sen' : 'Cin Ali'}</div>
                    <div className="chat-bubble-bubble"><ReactMarkdown>{msg.text}</ReactMarkdown></div>
                  </div>
                </div>
              ))}
              {qaWaitingAI && isLoading && (
                <div className="chat-bubble-row teacher">
                  <div className="chat-bubble-avatar">🤖</div>
                  <div className="chat-bubble-content">
                    <div className="chat-bubble-label">Cin Ali</div>
                    <div className="chat-bubble-bubble loading">
                      <span className="dot"/><span className="dot"/><span className="dot"/>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <form className="chat-input-form-bar" onSubmit={handleAskQuestion}>
              <div style={{ position: 'relative', flex: 1, display: 'flex', alignItems: 'center' }}>
                <input type="text" value={chatInput} onChange={e => setChatInput(e.target.value)}
                  placeholder={isQuotaExceeded ? 'Günlük limit doldu' : 'Sorun nedir?'}
                  maxLength={3500} disabled={isLoading || isQuotaExceeded} style={{ width: '100%', paddingRight: 80 }} />
                {chatInput.length > 0 && <span className="input-char-indicator">{chatInput.length}/3500</span>}
              </div>
              <button type="submit" className="send-btn" disabled={!chatInput.trim() || isLoading || isQuotaExceeded}>➔</button>
            </form>

            {qaAnswerDone && !isLoading && (
              <div className="chat-session-resolve-actions">
                <button className="chat-resolve-btn understood" onClick={handleUnderstood}>✅ Anladım, Derse Dön</button>
                <button className="chat-resolve-btn retry" onClick={handleNotUnderstood}>❌ Anlamadım</button>
                <button type="button" onClick={handleDownloadPDF} className="chat-resolve-btn pdf" title="Notları Kaydet">📄</button>
              </div>
            )}
          </div>

          <div className="card understanding-feedback-sidebar-card">
            <h4>🤔 Bu konuyu ne kadar anladın?</h4>
            <div className="feedback-buttons-grid">
              <button className="feedback-option-btn understood" onClick={handleUnderstood}>🤩 Anladım</button>
              <button className="feedback-option-btn mixed"      onClick={handleNotUnderstood}>🤕 Karışık</button>
              <button className="feedback-option-btn lost"       onClick={handleNotUnderstood}>🧐 Hiç anlamadım</button>
              <button className="feedback-option-btn explain-diff" onClick={handleNotUnderstood}>📝 Farklı anlat</button>
            </div>
          </div>
        </div>
      )}

      {/* PDF print alanı */}
      {hasLesson && (
        <div className="cin-ali-print-area">
          <div className="print-header">
            <span className="print-badge">MinikCoder Academy</span>
            <h1 className="print-title">{course.title} — {lesson.title}</h1>
            <p className="print-desc">Cin Ali AI Öğretmen ders özeti</p>
          </div>
          <div className="print-slides-list">
            {slides.map((slide, idx) => (
              <div key={idx} className="print-slide-item">
                <div className="print-slide-index">Bölüm {idx + 1}</div>
                <div className="markdown-body"><ReactMarkdown>{slide.markdown}</ReactMarkdown></div>
              </div>
            ))}
          </div>
          {qaMessages.length > 0 && (
            <div className="print-qa-section">
              <h2 className="print-section-title">💬 Ders İçi Sorular & Yanıtlar</h2>
              {qaMessages.map((msg, idx) => (
                <div key={msg.id || idx} className={`print-qa-item print-qa-${msg.role}`}>
                  <div className="print-qa-label">{msg.role === 'student' ? '❓ Öğrenci:' : '💡 Cin Ali:'}</div>
                  <div className="markdown-body"><ReactMarkdown>{msg.text}</ReactMarkdown></div>
                </div>
              ))}
            </div>
          )}
          <div className="print-footer">© {new Date().getFullYear()} MinikCoder Academy</div>
        </div>
      )}
    </div>
  );
}